import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { 
  Box, Shield, CornerUpLeft, Loader2, 
  Package, CheckCircle, Clock, Archive,
  LayoutDashboard, Search, Filter as FilterIcon,
  ChevronRight, Info
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import TopbarNav from "../../components/TopbarNav";
import SidebarNav from "../../components/MasterSideBar"; 
import MyAssetsTable from "../../components/operationsAssociate/MyAssetsTable";
import ReturnAssetModal from "../../components/operationsAssociate/ReturnAssetModal";
import ViewAssetModal from "../../components/operationsAssociate/ViewAssetModal";
import PremiumHeader from "../../components/common/PremiumHeader";
import PremiumStatCard from "../../components/common/PremiumStatCard";
import PremiumCard from "../../components/common/PremiumCard";

const API_URL = "https://ims.holdthechild.net";

const PMAssetsPage = () => {
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [loading, setLoading] = useState(true);
    const [assets, setAssets] = useState([]);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [loggedInStaffId, setLoggedInStaffId] = useState(null);
    const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);

    useEffect(() => {
        const userStr = localStorage.getItem("user");
        if (userStr) {
            try {
                const user = JSON.parse(userStr);
                if (user?.id) setLoggedInStaffId(user.id);
                else navigate("/");
            } catch (e) {
                console.error("Error parsing user", e);
                navigate("/");
            }
        } else {
            navigate("/");
        }
    }, [navigate]);

    const fetchMyAssets = useCallback(async () => {
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                toast.error("Session expired. Please login again.");
                navigate("/");
                return;
            }
            const response = await axios.get(`${API_URL}/api/assets/my-assets`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setAssets(response.data.data || []);
        } catch (err) {
            console.error("Failed to fetch assets:", err);
            toast.error("Failed to load assets.");
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => { 
        if (loggedInStaffId) fetchMyAssets(); 
    }, [loggedInStaffId, fetchMyAssets]);

    const handleOpenReturnModal = (asset) => { 
        setSelectedAsset(asset); 
        setIsReturnModalOpen(true); 
    };

    const handleOpenViewModal = (asset) => { 
        setSelectedAsset(asset); 
        setIsViewModalOpen(true); 
    };

    return (
        <div className="flex min-h-screen bg-gray-50 font-sans overflow-hidden">
            <ToastContainer theme="colored" position="top-right" />
            <SidebarNav 
                visible={sidebarOpen} 
                onClose={() => setSidebarOpen(false)} 
                collapsed={sidebarCollapsed} 
                setCollapsed={setSidebarCollapsed} 
                onLogout={() => { localStorage.clear(); navigate("/"); }} 
            />

            <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
                <TopbarNav onSidebarToggle={() => setSidebarOpen(true)} />
                <main className="flex-1 p-4 md:p-8 mt-16 lg:mt-20 overflow-y-auto">
                    <div className="max-w-7xl mx-auto space-y-8">
                        
                        <div className="flex flex-col items-center text-center mb-12">
                            <PremiumHeader 
                                title="Asset" 
                                lastWordAccent="Custody" 
                                subtitle="Comprehensive inventory of equipment and technology currently assigned to your professional care." 
                            />
                        </div>

                        {/* Quick Stats Summary */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                                <PremiumStatCard 
                                    title="Total Custody" 
                                    value={assets.length} 
                                    icon={<Box className="text-orange-600" />} 
                                    iconBg="bg-orange-50"
                                    description="All assigned items"
                                />
                            </motion.div>
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                                <PremiumStatCard 
                                    title="Active / Verified" 
                                    // UPDATED: Changed 'Assigned' to 'Active' to match new workflow status
                                    value={assets.filter(a => a.status === 'Active').length} 
                                    icon={<Shield className="text-blue-600" />} 
                                    iconBg="bg-blue-50"
                                    description="Accepted & in use"
                                />
                            </motion.div>
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                                <PremiumStatCard 
                                    title="Pending Action" 
                                    // UPDATED: Count items that need user attention (Acceptance or Return)
                                    value={assets.filter(a => a.status === 'Pending Acceptance' || a.status === 'Pending Return').length} 
                                    icon={<Clock className="text-amber-600" />} 
                                    iconBg="bg-amber-50"
                                    description="Requires your input"
                                />
                            </motion.div>
                        </div>

                        {/* Main Asset Table */}
                        <PremiumCard icon={<Archive size={18} />} title="Custody Registry">
                            <div className="min-h-[400px]">
                                {loading ? (
                                    <div className="py-24 flex flex-col items-center justify-center">
                                        <Loader2 className="w-10 h-10 animate-spin text-orange-500 mb-4" />
                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Synchronizing Registry...</p>
                                    </div>
                                ) : (
                                    <div className="p-2">
                                        {/* 
                                            CRITICAL UPDATE: Added refreshAssets={fetchMyAssets} 
                                            This allows the Accept/Reject buttons in the table to refresh the page data immediately.
                                        */}
                                        <MyAssetsTable 
                                            assets={assets} 
                                            onReturn={handleOpenReturnModal} 
                                            onView={handleOpenViewModal} 
                                            loggedInStaffId={loggedInStaffId} 
                                            refreshAssets={fetchMyAssets}
                                        />
                                    </div>
                                )}
                            </div>
                        </PremiumCard>
                    </div>
                </main>
            </div>

            {/* Modals */}
            <AnimatePresence>
                {isReturnModalOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsReturnModalOpen(false)} className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
                        <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }} className="relative bg-white rounded-[2.5rem] shadow-2xl w-full max-w-lg overflow-hidden border border-gray-100">
                             <ReturnAssetModal asset={selectedAsset} closeModal={() => { setIsReturnModalOpen(false); fetchMyAssets(); }} />
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {isViewModalOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsViewModalOpen(false)} className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
                        <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }} className="relative bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl overflow-hidden border border-gray-100">
                            <ViewAssetModal asset={selectedAsset} closeModal={() => setIsViewModalOpen(false)} />
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};
export default PMAssetsPage;